import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { AuthContext } from "../context/AuthContext";
import { completeKakaoLogin } from "../services/kakao";
import "./KakaoCallback.css";

/**
 * Where Kakao drops the customer back after consent.
 *
 * Most of the time this is a spinner that redirects. The interesting branch is
 * the e-mail prompt: a Kakao account can be registered with a phone number and
 * no e-mail at all, and since every order update we send is an e-mail, we ask
 * for one BEFORE the account is created rather than leaving a customer who can
 * never hear about their order.
 */
const KakaoCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useContext(AuthContext);

  const [state, setState] = useState("working"); // working | needEmail | failed
  const [email, setEmail] = useState("");
  const [signupToken, setSignupToken] = useState(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const ran = useRef(false);

  const finish = (data) => {
    localStorage.setItem("authToken", data.token);
    localStorage.setItem("userInfo", JSON.stringify(data.user));
    login(data.user, data.token);
    toast.success(t("auth.loginSuccess"));
    const next = sessionStorage.getItem("kakaoNext") || "/";
    sessionStorage.removeItem("kakaoNext");
    navigate(next, { replace: true });
  };

  useEffect(() => {
    // React 18 StrictMode double-invokes effects in dev; a Kakao code is
    // single-use, so guard against spending it twice.
    if (ran.current) return;
    ran.current = true;

    const code = params.get("code");
    const denied = params.get("error");

    if (denied || !code) {
      setState("failed");
      setMessage(t("auth.kakaoCancelled"));
      return;
    }

    completeKakaoLogin({ code }).then((res) => {
      if (res.ok) return finish(res);
      if (res.needsEmail) {
        setSignupToken(res.signupToken);
        setState("needEmail");
        return;
      }
      setState("failed");
      setMessage(res.message || t("auth.loginFailed"));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submitEmail = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error(t("auth.invalidEmail"));
      return;
    }
    setSaving(true);
    const res = await completeKakaoLogin({ email: value, signupToken });
    setSaving(false);

    if (res.ok) return finish(res);
    // The address they typed already belongs to someone — make them prove it.
    if (res.needsPasswordLink) {
      setState("failed");
      setMessage(res.message);
      return;
    }
    toast.error(res.message || t("auth.loginFailed"));
  };

  return (
    <div className="kkc-page">
      <div className="kkc-card">
        {state === "working" && (
          <>
            <div className="kkc-spinner" />
            <p className="kkc-msg">{t("auth.kakaoConnecting")}</p>
          </>
        )}

        {state === "needEmail" && (
          <form onSubmit={submitEmail}>
            <h2 className="kkc-title">{t("auth.oneMoreStep")}</h2>
            <p className="kkc-sub">{t("auth.kakaoNoEmail")}</p>
            <input
              className="kkc-input"
              type="email"
              autoFocus
              value={email}
              placeholder={t("auth.emailPlaceholder")}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="kkc-btn" type="submit" disabled={saving}>
              {saving ? t("checkout.saving") : t("auth.continue")}
            </button>
          </form>
        )}

        {state === "failed" && (
          <>
            <div className="kkc-fail">!</div>
            <p className="kkc-msg">{message}</p>
            <Link className="kkc-btn kkc-btn-link" to="/login">
              {t("auth.backToLogin")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default KakaoCallback;
